<template>
  <footer class="app-footer">
    <div class="footer-inner">
      <div class="footer-section footer-dev">
        <h4 class="footer-heading">{{ t('sidebar.developers') }}</h4>
        <div class="footer-links">
          <router-link to="/api-keys">{{ t('sidebar.apiKeys') }}</router-link>
          <router-link to="/docs/inbound">{{ t('sidebar.inboundDocs') }}</router-link>
          <router-link to="/api-logs">{{ t('sidebar.apiLogs') }}</router-link>
          <router-link to="/webhooks">{{ t('sidebar.webhooks') }}</router-link>
          <router-link to="/sandbox">{{ t('sidebar.sandbox') }}</router-link>
        </div>
      </div>
      <div class="footer-section footer-legal">
        <h4 class="footer-heading">{{ t('sidebar.legal') }}</h4>
        <div class="footer-links">
          <router-link to="/legal/privacy-policy">{{ t('home.footer.privacyPolicy') }}</router-link>
          <router-link to="/legal/terms-of-service">{{ t('home.footer.termsOfService') }}</router-link>
          <router-link to="/legal/tariffs">{{ t('home.footer.limits') }}</router-link>
          <router-link to="/legal/personal-data-consent">{{ t('home.footer.personalDataConsent') }}</router-link>
        </div>
      </div>
      <div class="footer-section footer-brand">
        <div class="footer-logo">
          <img src="@/assets/logo-alfabit.svg" alt="AlfaBit" class="footer-logo-img theme-logo" />
          <span class="footer-logo-text">Payments</span>
        </div>
        <p class="footer-copyright">© {{ currentYear }} AlfaBit Payments</p>
      </div>
    </div>
    <div class="footer-bottom">
      <router-link class="footer-offer-link" to="/legal/user-agreement">
        {{ t('home.footer.userAgreement') }}
      </router-link>
      <span>{{ t('nbkrLicense') }}</span>
    </div>
  </footer>
</template>

<script setup lang="ts">
import { useI18n } from '@/composables/useI18n'

const { t } = useI18n()
const currentYear = new Date().getFullYear()
</script>

<style scoped>
.app-footer {
  border-top: 1px solid var(--border-primary);
  background: #0b0e11;
  margin-top: auto;
  color: #848e9c;
}

[data-theme="light"] .app-footer {
  background: #f7faf8;
  border-top: 1px solid var(--border-primary);
  color: var(--text-secondary);
}

.footer-inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 2rem 1.5rem;
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 2rem;
}

.footer-heading {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  margin: 0 0 0.75rem;
}

.footer-links {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.footer-links a {
  font-size: 0.8125rem;
  color: var(--text-secondary);
  text-decoration: none;
  padding: 0.25rem 0;
  transition: color 0.15s ease;
}

.footer-links a:hover {
  color: var(--color-brand, var(--primary-green));
}

[data-theme="light"] .footer-links a:hover {
  color: var(--color-brand-deep, var(--primary-green));
}

.footer-brand {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
}

.footer-logo {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.footer-logo-img {
  width: 96px;
  height: auto;
  object-fit: contain;
}

.footer-logo-text {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-secondary);
}

.footer-copyright {
  font-size: 0.75rem;
  color: var(--text-tertiary);
  margin: 0;
}

.footer-bottom {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0.75rem 2rem 1rem;
  font-size: 0.75rem;
  color: var(--text-tertiary);
  border-top: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.footer-offer-link {
  color: var(--text-secondary);
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.footer-offer-link:hover {
  color: var(--color-brand, var(--primary-green));
}

@media (max-width: 768px) {
  .footer-inner {
    grid-template-columns: 1fr 1fr;
    padding: 1.5rem 1rem;
    gap: 1.25rem;
    padding-bottom: calc(1.5rem + 72px);
  }

  .footer-heading {
    font-size: 0.7rem;
    margin-bottom: 0.5rem;
  }

  .footer-links a {
    font-size: 0.8125rem;
    padding: 0.2rem 0;
  }

  .footer-brand {
    grid-column: 1 / -1;
    align-items: center;
    text-align: center;
    padding-top: 0.75rem;
    border-top: 1px solid var(--border-primary);
  }

  .footer-logo-text {
    font-size: 0.8125rem;
  }

  .footer-copyright {
    font-size: 0.7rem;
  }

  .footer-bottom {
    padding: 0.6rem 1rem 0.8rem;
    font-size: 0.7rem;
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
}
</style>
